/**
 * What a Roll leaves behind: a serialisable record with everything the Roll Log (#20) shows,
 * the spec the sheet supplied, the choices made and the result, linked to the Roll it rerolled.
 */
import type { CheckChoices, CheckResult, CheckSpec } from "./check";
import { describeCheck } from "./check";
import type { DamageResult } from "./damage";
import { describeDamage } from "./damage";
import type { RolledFormula } from "./dice";

/** Where a Roll came from, for the Roll Log and for linking a damage Roll to its strike. */
export interface RollOrigin {
  kind: "statistic" | "strike" | "spell" | "entry" | "session" | "inline";
  /** The statistic slug, the strike key, the spell UUID, the Entry UUID, or the Session item id. */
  key: string | null;
  name: string | null;
}

interface RollRecordBase {
  id: string;
  /** ISO timestamp. */
  at: string;
  label: string;
  origin: RollOrigin;
  physical: boolean;
  /** The Roll this one rerolled (a Hero Point reroll), or null. */
  rerollOf: string | null;
  heroPoint: boolean;
}

export interface CheckRollRecord extends RollRecordBase {
  kind: "check";
  spec: CheckSpec;
  choices: CheckChoices;
  result: CheckResult;
}

export interface DamageRollRecord extends RollRecordBase {
  kind: "damage";
  formula: string;
  criticalFormula: string | null;
  critical: boolean;
  result: DamageResult;
}

export interface InlineRollRecord extends RollRecordBase {
  kind: "inline";
  formula: string;
  flavor: string | null;
  result: RolledFormula;
}

export type RollRecord = CheckRollRecord | DamageRollRecord | InlineRollRecord;

export function newRollId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `roll-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** One line for the Roll Log: "Athletics: 17 +7 = 24 vs DC 20: success". */
export function summarizeRoll(record: RollRecord): string {
  const tags = [record.physical ? "physical dice" : "", record.heroPoint ? "Hero Point reroll" : ""].filter(Boolean);
  const suffix = tags.length > 0 ? ` (${tags.join(", ")})` : "";
  switch (record.kind) {
    case "check": return `${record.label}: ${describeCheck(record.result)}${suffix}`;
    case "damage": return `${record.label}: ${describeDamage(record.result)}${suffix}`;
    case "inline": return `${record.label}: ${record.result.total}${suffix}`;
  }
}
